import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface EstablishmentContact {
  id?: number;
  name: string;
  phone: string;
  email?: string;
  is_primary: boolean;
  notes?: string;
  created_at?: Date;
}

export interface Establishment {
  id: number;
  company_id: number;
  position_id: number;
  name: string;
  description?: string;
  address?: string;
  is_active: boolean;
  contacts: EstablishmentContact[];
  establishment_block_id:number;
  position?: {
    id: number;
    name: string;
  };
  company?: {
    id: number;
    name: string;
  };
  created_at: Date;
  updated_at: Date;
}

export interface CreateEstablishmentDto {
  company_id: number;
  position_id: number;
  name: string;
  description?: string;
  establishment_block_id?:number;
  address?: string;
  is_active?: boolean;
  contacts: EstablishmentContact[];
}

export interface UpdateEstablishmentDto {
  name?: string;
  description?: string;
  establishment_block_id?:number;
  address?: string;
  is_active?: boolean;
  contacts?: EstablishmentContact[];
}

export interface EstablishmentFilter {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  companyId?: number;
  positionId?: number;
  establishmentBlockId?:number;
  search?: string;
  status?: 'active' | 'inactive';
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class EstablishmentService {
  private readonly apiUrl = `${environment.apiUrl}/establishments`;
  private cache = new Map<string, Observable<any>>();

  constructor(private http: HttpClient) {}

  /**
   * Criar novo estabelecimento
   */
  create(dto: CreateEstablishmentDto): Observable<Establishment> {
    return this.http.post<Establishment>(this.apiUrl, dto).pipe(
      tap(response => {
        
        this.clearCache();
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Listar estabelecimentos com paginação e filtros
   */
  findAll(filters?: EstablishmentFilter): Observable<PaginatedResponse<Establishment>> {
    const params = this.buildParams(filters);
    const cacheKey = `findAll_${params.toString()}`;
    
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)!;
    }
    
    const request = this.http.get<PaginatedResponse<Establishment>>(this.apiUrl, { params }).pipe(
      shareReplay(1),
      catchError(this.handleError)
    );
    
    this.cache.set(cacheKey, request);
    setTimeout(() => this.cache.delete(cacheKey), 5 * 60 * 1000);
    
    return request;
  }

  /**
   * Buscar estabelecimento por ID
   */
  findOne(id: number): Observable<Establishment> {
    return this.http.get<Establishment>(`${this.apiUrl}/${id}`).pipe(
      
      catchError(this.handleError)
    );
  }

  /**
   * Buscar estabelecimentos por posto
   */
  findByPosition(positionId: number): Observable<Establishment[]> {
    return this.http.get<Establishment[]>(`${this.apiUrl}/position/${positionId}`).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Atualizar estabelecimento
   */
  update(id: number, dto: UpdateEstablishmentDto): Observable<Establishment> {
    return this.http.put<Establishment>(`${this.apiUrl}/${id}`, dto).pipe(
      tap(response => {
        this.clearCache();
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Remover estabelecimento (soft delete)
   */
  remove(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`).pipe(
      tap(() => {
        this.clearCache();
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Listar contatos do estabelecimento
   */
  getContacts(id: number): Observable<EstablishmentContact[]> {
    return this.http.get<EstablishmentContact[]>(`${this.apiUrl}/${id}/contacts`).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Limpar cache
   */
  clearCache(): void {
    this.cache.clear();

  }

  /**
   * Construir parâmetros HTTP
   */
  private buildParams(filters?: EstablishmentFilter): HttpParams {
    let params = new HttpParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params = params.append(key, value.toString());
        }
      });
    }
    
    return params;
  }

  /**
   * Tratamento de erros
   */
  private handleError(error: any): Observable<never> {
    console.error('Erro no EstablishmentService:', error);
    const message = error.error?.message || error.message || 'Erro desconhecido';
    return throwError(() => new Error(message));
  }
}